import {
  takeEvery,
  call,
  put,
  select,
  getContext,
  race,
  delay,
  take,
} from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import {
  deleteLeaveRoom,
  deleteLeaveRoomSuccess,
  deleteLeaveRoomFailure,
  closeRoom,
  toggleStarred,
  toggleMuted,
  toggleArchived,
  updateDrawerRoomFlagState,
  userBlocked,
  openBlockModal,
  createGroupChatFromRoom,
  toggleStartNewMessage,
  renameRoom,
  renameRoomSuccess,
  renameRoomFailure,
  setActiveRoomData,
  ignoreRequest,
  undoIgnoreRequest,
  reportMessageSpam,
  acceptRequest,
  selectRoom,
} from '../../actions/room';
import { setDrawerFilter } from '../../actions/drawer';
import {
  requestPuppy,
  isApiError,
  createFormData,
  XhrResponse,
} from '@wix/da-http-client';
import { fetchRoomById, makePapiUser } from '../../helpers';
import { RoomFlags, RoomItem, RoomType } from '../../../types';
import {
  PapiDamzMessageReport,
  PapiDamzRoomDelete,
  PapiDamzRoomLeave,
  PapiDamzRoomRename,
  PapiRequestDamzMessageReport,
  PapiRequestDamzRoomDelete,
  PapiRequestDamzRoomLeave,
  PapiRequestDamzRoomRename,
  PapiUser,
} from '@wix/da-papi-types';
import { getActiveRoomId, getActiveRoomItem } from '../../selectors/room';
import { deleteItems } from '@wix/da-shared-react/pkg/Stream/withOffset/actions';
import {
  drawerStreamId,
  roomItemStreamGroupId,
  TEMP_DRAFT_ROOM_ID,
} from '../../../constants';
import { getDrawerActiveFilter } from '../../selectors/drawer';
import { popModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { Database } from '@wix/da-papi-types/lib/supabase';
import { getCurrentUser } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { SupabaseClient } from '@supabase/supabase-js';
import {
  addErrorNotification,
  addNotification,
} from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import i18next from 'i18next';
import { onActivity } from '../../actions/global';
import { addDraftUser, removeDraftUserAll } from '../../actions/drafts';
import { findItemInStreams } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { withCursor } from '@wix/da-shared-react/pkg/Stream';
import { MODAL_TYPES } from '../../../project-tools';
import { getChatters } from '../helpers';
import { SettableRoomFlagNames } from './room-management';

function* handleSetRoomFlag(
  room_id: string,
  whichFlag: SettableRoomFlagNames,
  newValue: boolean
) {
  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');
  const response = yield client.rpc('set_room_participant_flag', {
    p_room_id: room_id,
    p_bit: RoomFlags[whichFlag],
    p_onoff: newValue,
  });

  if (!response || isApiError(response)) {
    ddt.alert(
      'damz',
      '[damz] Failed to set_room_participant_flag',
      ['damz', 'db'],
      response.error
    );
    const { code, message } = response?.error || {};
    yield put(
      addErrorNotification(
        code && message
          ? i18next.t('damz:damz.room.flags.set.error', { code, message })
          : i18next.t('damz:damz.room.flags.set.error.generic')
      )
    );
  }
  return response;
}

function* handleDeleteLeaveRoom(action: ReturnType<typeof deleteLeaveRoom>) {
  const { room_id, isDelete } = action.payload;
  const chatters: string[] = yield call(getChatters, room_id);
  try {
    const roomData:
      | Database['damz']['Functions']['get_conversations_by_room_id']['Returns'][0]
      | false = yield fetchRoomById(room_id);

    if (!roomData) {
      yield put(deleteLeaveRoomFailure(room_id));
      return;
    }

    const currentUser: PapiUser = yield select(getCurrentUser);
    if (!currentUser) {
      yield put(deleteLeaveRoomFailure(room_id));
      return;
    }

    const isOwner = roomData.owner_id === currentUser.useridUuid;

    // Non-owners can leave
    let response: XhrResponse<PapiDamzRoomLeave | PapiDamzRoomDelete | null> =
      null;
    if (
      roomData.room_type === RoomType.DM ||
      roomData.room_type === RoomType.MULTI ||
      (roomData.room_type === RoomType.COMMUNITY && !isOwner) ||
      (roomData.room_type === RoomType.COMMUNITY && isOwner && !isDelete)
    ) {
      response = yield call(
        requestPuppy,
        {
          method: 'post',
          url: 'room/leave',
          data: createFormData<PapiRequestDamzRoomLeave>({
            room_id: room_id,
            chatters,
          }),
        },
        () => ({ success: true }),
        'damz'
      );
    }

    // Owner can delete
    if (roomData.room_type === RoomType.COMMUNITY && isOwner && isDelete) {
      response = yield call(
        requestPuppy,
        {
          method: 'post',
          url: 'room/delete',
          data: createFormData<PapiRequestDamzRoomDelete>({ room_id: room_id }),
        },
        () => ({ success: true }),
        'damz'
      );
    }

    if (!response || isApiError(response) || !response.success) {
      yield put(deleteLeaveRoomFailure(room_id));
      return;
    }

    yield put(deleteLeaveRoomSuccess(room_id));

    // Next we need to delete the room from the drawer stream
    const activeFilter = yield select(getDrawerActiveFilter);
    const streamId = `${drawerStreamId}${activeFilter}`;
    yield put(
      deleteItems({
        streamId,
        items: [{ room: { room_id: room_id } }],
        isEqual(item1: RoomItem, item2: RoomItem) {
          return item1.room.room_id === item2.room.room_id;
        },
      })
    );

    // If active room is the one that was deleted, we need to close the room
    const activeRoomId = yield select(getActiveRoomId);
    if (activeRoomId === room_id) {
      yield put(closeRoom());
    }

    // finally pop the modal
    yield put(popModal());
  } catch (err) {
    yield put(deleteLeaveRoomFailure(room_id));
  }
}

function* handleToggleStarred(action: ReturnType<typeof toggleStarred>) {
  const { room_id, isStarred } = action.payload;
  yield handleSetRoomFlag(room_id, 'starred', isStarred);
  yield put(updateDrawerRoomFlagState(room_id, 'starred', isStarred));
  yield put(onActivity(room_id));
}

function* handleToggleMuted(action: ReturnType<typeof toggleMuted>) {
  const { room_id, isMuted } = action.payload;
  yield handleSetRoomFlag(room_id, 'muted', isMuted);
  yield put(updateDrawerRoomFlagState(room_id, 'muted', isMuted));
  yield put(onActivity(room_id));
}

function* handleToggleArchived(action: ReturnType<typeof toggleArchived>) {
  const { room_id, isArchived } = action.payload;
  yield handleSetRoomFlag(room_id, 'archived', isArchived);
  yield put(updateDrawerRoomFlagState(room_id, 'archived', isArchived));
  if (isArchived) {
    const activeFilter = yield select(getDrawerActiveFilter);
    const streamId = `${drawerStreamId}${activeFilter}`;
    if (activeFilter !== 'archived') {
      yield put(
        deleteItems({
          streamId,
          items: [{ room: { room_id } }],
          isEqual(item1: RoomItem, item2: RoomItem) {
            return item1.room.room_id === item2.room.room_id;
          },
        })
      );
    }
  }
}

function* handleUserBlocked(action: ReturnType<typeof userBlocked>) {
  const { room_id } = action.payload;
  yield put(deleteLeaveRoom(room_id, true));
}

function* handleOpenBlockModal(action: ReturnType<typeof openBlockModal>) {
  const { room_id } = action.payload;
  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');
  const currentUser: PapiUser = yield select(getCurrentUser);

  const response: {
    data:
      | Database['damz']['Functions']['get_room_owning_user']['Returns']
      | null;
    error: any;
  } = yield client.rpc('get_room_owning_user', {
    p_room_id: room_id,
  });

  if (response.error || !response.data?.[0]) {
    yield put(
      addErrorNotification(i18next.t('damz:damz.room.block.error.roomNotFound'))
    );
    return;
  }

  const roomOwner = response.data[0];

  // Check if the current user is trying to block themselves
  if (roomOwner.user_uuid === currentUser.useridUuid) {
    yield put(
      addErrorNotification(
        i18next.t('damz:damz.room.block.error.cannotBlockYourself')
      )
    );
    return;
  }

  const user = makePapiUser(
    roomOwner.username,
    roomOwner.user_uuid,
    roomOwner.user_id_int,
    roomOwner.usericon,
    roomOwner.symbol,
    roomOwner.banned_left
  );

  yield put(
    pushModal(ModalType.USER_BLOCK, {
      params: {
        user,
        dispatchAction: userBlocked(room_id),
      },
    })
  );
}

// Only supports DMs for now
function* handleCreateGroupChatFromRoom(
  action: ReturnType<typeof createGroupChatFromRoom>
) {
  const { room } = action.payload;
  if (room.dmUser) {
    yield put(toggleStartNewMessage());
    yield put(removeDraftUserAll(TEMP_DRAFT_ROOM_ID));
    yield put(addDraftUser(room.dmUser, TEMP_DRAFT_ROOM_ID));
  }
}

function* handleReportMessageSpam(
  action: ReturnType<typeof reportMessageSpam>
) {
  const { room_id, message_id } = action.payload;
  const activeRoomId = yield select(getActiveRoomId);
  let roomInfo: RoomItem | undefined;

  if (activeRoomId === room_id) {
    // Get info from activeRoomItem
    const activeRoomItem = yield select(getActiveRoomItem);
    roomInfo = activeRoomItem;
  } else {
    // Get info from drawer stream
    roomInfo = yield select(state =>
      findItemInStreams(
        state,
        roomItemStreamGroupId,
        (item: RoomItem) => item.room.room_id === room_id
      )
    );
  }

  if (!roomInfo) {
    yield put(
      addErrorNotification(
        i18next.t('damz:damz.room.report.error.roomNotFound')
      )
    );
    return;
  }

  const client: SupabaseClient<Database, 'damz'> =
    yield getContext('supaClient');
  const currentUser: PapiUser = yield select(getCurrentUser);

  const reportableResponse: {
    data:
      | Database['damz']['Functions']['get_room_owning_user']['Returns']
      | null;
    error: any;
  } = yield client.rpc('get_room_owning_user', {
    p_room_id: roomInfo.room.room_id,
  });

  if (reportableResponse.error) {
    yield put(
      addErrorNotification(
        i18next.t('damz:damz.room.report.error.roomNotFound')
      )
    );
    return;
  }

  const reportableUser = reportableResponse.data?.[0];

  if (
    reportableUser?.user_uuid === currentUser.useridUuid &&
    roomInfo.room.room_type === RoomType.DM
  ) {
    yield put(
      addErrorNotification(
        i18next.t('damz:damz.room.report.error.cannotReportYourself')
      )
    );
  }

  const data = createFormData<PapiRequestDamzMessageReport>({
    message_id,
  });

  const response: PapiDamzMessageReport = yield call(
    requestPuppy,
    {
      method: 'post',
      url: `/message/report`,
      data,
    },
    undefined,
    'damz'
  );

  if (!response || isApiError(response) || !response.success) {
    yield put(
      addErrorNotification(i18next.t('damz:damz.room.report.error.generic'))
    );
  }

  yield put(popModal());
  yield put(
    pushModal(MODAL_TYPES.REPORT_SPAM_DONE, {
      params: {
        user: reportableUser,
      },
    })
  );
}

function* handleRenameRoom(action: ReturnType<typeof renameRoom>) {
  const { room_id, name } = action.payload;
  const chatters: string[] = yield call(getChatters, room_id);

  try {
    const formData = createFormData<PapiRequestDamzRoomRename>({
      room_id,
      name,
      chatters,
    });

    const response: XhrResponse<PapiDamzRoomRename> = yield call(
      requestPuppy,
      {
        method: 'post',
        url: 'room/rename',
        data: formData,
      },
      undefined,
      'damz'
    );

    if (!response || isApiError(response)) {
      const errorDescription =
        response &&
        typeof response === 'object' &&
        'errorDescription' in response
          ? response.errorDescription
          : 'Unknown error';
      yield put(renameRoomFailure(room_id, errorDescription));
      yield put(
        addErrorNotification(
          i18next.t('damz:damz.room.rename.error', { errorDescription })
        )
      );
      return;
    }

    if (!response.success) {
      const errorDescription = response.errorDescription ?? 'Rename failed';
      yield put(renameRoomFailure(room_id, errorDescription));
      yield put(
        addErrorNotification(
          i18next.t('damz:damz.room.rename.error', { errorDescription })
        )
      );
      return;
    }

    yield put(renameRoomSuccess(room_id, name));

    // Update the room name in the drawer stream
    const activeFilter = yield select(getDrawerActiveFilter);
    const streamId = `${drawerStreamId}${activeFilter}`;

    // Find and update the room item in the stream
    const roomItem = yield select(state =>
      findItemInStreams(
        state,
        roomItemStreamGroupId,
        (item: RoomItem) => item.room.room_id === room_id
      )
    );

    if (roomItem) {
      yield put(
        withCursor.actions.replaceItem({
          streamId,
          newItem: {
            ...roomItem,
            room: {
              ...roomItem.room,
              name,
            },
          },
          predicate: (item: RoomItem) => item.room.room_id === room_id,
        })
      );
    }

    // Update active room details if this is the active room
    const activeRoomId = yield select(getActiveRoomId);
    if (activeRoomId === room_id) {
      const activeRoomItem = yield select(getActiveRoomItem);
      if (activeRoomItem) {
        yield put(
          setActiveRoomData({
            ...activeRoomItem,
            room: {
              ...activeRoomItem.room,
              name,
            },
          })
        );
      }
    }

    yield put(popModal());
  } catch (error) {
    const errorDescription =
      error instanceof Error ? error.message : 'Unknown error';
    yield put(renameRoomFailure(room_id, errorDescription));
    yield put(
      addErrorNotification(
        i18next.t('damz:damz.room.rename.error', { errorDescription })
      )
    );
  }
}

function* handleIgnoreRequest(action: ReturnType<typeof ignoreRequest>) {
  const { room_id } = action.payload;

  // Find the room item in the stream before removing it
  const roomItem = yield select(state =>
    findItemInStreams(
      state,
      roomItemStreamGroupId,
      (item: RoomItem) => item.room.room_id === room_id
    )
  );

  if (!roomItem) {
    return;
  }

  // Immediately remove the room from the stream
  const activeFilter = yield select(getDrawerActiveFilter);
  const streamId = `${drawerStreamId}${activeFilter}`;
  yield put(
    deleteItems({
      streamId,
      items: [{ room: { room_id } }],
      isEqual(item1: RoomItem, item2: RoomItem) {
        return item1.room.room_id === item2.room.room_id;
      },
    })
  );

  // Show notification
  yield put(addNotification('ignore-request', { room_id }));

  // Race between 5 second delay and undo action
  const result = yield race({
    undo: take(undoIgnoreRequest),
    timeout: delay(5000),
  });

  if (result.undo && result.undo.payload.room_id === room_id) {
    // User pressed undo - restore the room
    yield put(
      withCursor.actions.insertItemsAtOffsetAndSetCursor({
        streamId,
        items: [roomItem],
        offset: 0,
      })
    );
  } else {
    yield put(deleteLeaveRoom(room_id, true));
  }
}

function* handleAcceptRequest(action: ReturnType<typeof acceptRequest>) {
  const { room_id } = action.payload;
  try {
    yield handleSetRoomFlag(room_id, 'request', false);
    yield put(setDrawerFilter('all'));
    yield put(selectRoom(room_id, undefined, false));
  } catch (error) {
    yield put(
      addErrorNotification(i18next.t('damz:damz.room.error.acceptRequest'))
    );
  }
}

export function* roomActionsSaga() {
  yield takeEvery(deleteLeaveRoom, handleDeleteLeaveRoom);
  yield takeEvery(toggleStarred, handleToggleStarred);
  yield takeEvery(toggleMuted, handleToggleMuted);
  yield takeEvery(toggleArchived, handleToggleArchived);
  yield takeEvery(userBlocked, handleUserBlocked);
  yield takeEvery(openBlockModal, handleOpenBlockModal);
  yield takeEvery(createGroupChatFromRoom, handleCreateGroupChatFromRoom);
  yield takeEvery(renameRoom, handleRenameRoom);
  yield takeEvery(ignoreRequest, handleIgnoreRequest);
  yield takeEvery(acceptRequest, handleAcceptRequest);
  yield takeEvery(reportMessageSpam, handleReportMessageSpam);
}
